<script setup lang="ts">
const props = withDefaults(defineProps<{
  type?: 'button' | 'submit'
  variant?: 'primary' | 'success' | 'error' | 'warning' | 'dark' | 'info'
  size?: 'xs' | 'lg'
  loading?: boolean
  disabled?: boolean
  text?: string
  icon?: string
}>(), {
  type: 'button',
  variant: 'primary',
})

const emit = defineEmits<{
  (e: 'click'): void
}>()

const iconClass = computed(() => {
  if (!props.icon)
    return undefined

  if (props.icon?.includes('i-tabler-'))
    return props.icon

  return `i-tabler-${props.icon}`
})
</script>

<template>
  <button
    :type="type"
    class="btn"
    :class="{
      'btn-xs text-xs': size === 'xs',
      'btn-lg text-lg': size === 'lg',
      'disabled': disabled || loading,
      'btn-primary': variant === 'primary',
      'btn-success': variant === 'success',
      'btn-error': variant === 'error',
      'btn-warning': variant === 'warning',
      'btn-dark': variant === 'dark',
      'btn-info': variant === 'info',
    }"
    :disabled="disabled || loading"
    @click="emit('click')"
  >
    <slot v-if="$slots.default" name="default" />
    <div
      v-else
      class="flex items-center justify-center gap-2"
    >
      <b-loading v-if="loading" />
      <div v-if="!loading && icon" class="text-4" :class="iconClass" />
      <span v-if="text">{{ text }}</span>
    </div>
  </button>
</template>

<style lang="scss" scoped>
.btn {
  @apply inline-flex items-center justify-center gap-2 px-2 py-2 rounded-1 text-3 text-white transition-all duration-200;

  &.btn-xs {
    @apply px-1 py-1 text-4;
  }

  &.btn-lg {
    @apply px-6 py-3 text-6;
  }

  &.btn-primary {
    @apply bg-primary-500 text-white;
  }

  &.btn-info {
    @apply bg-info-500 text-white;
  }

  &.btn-success {
    @apply bg-success-900 text-white;
  }

  &.btn-error {
    @apply bg-error-500 text-white;
  }

  &.btn-warning {
    @apply bg-warning-500 text-white;
  }

  &.btn-dark {
    @apply bg-dark-500 text-white;
  }

  &:active {
    @apply scale-95;
  }

  &:disabled {
    @apply opacity-50 cursor-not-allowed;
  }
}
</style>
